import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React from 'react';
import ExportPensionForm, {
  ExportPensionFormValues,
} from './ExportPensionForm';
import { Col, Row } from 'antd';
import {
  useExportNowPension,
  useExportPapdisPension,
  useExportPension,
  useExportPensionIreland,
} from '@nxsys/hooks/api/report';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  nowLikePensionProviders,
  PensionProvidersNameEnum,
} from '@nxsys/constants/AppEnums';
import moment from 'moment';

// The NEST date pickers may hand back a Moment (calendar pick) or an ISO
// string (prefill). Normalise to strict YYYY-MM-DD for the export DTO.
const toIsoDate = (v?: unknown): string | undefined => {
  if (!v) return undefined;
  const m = moment(v as moment.MomentInput);
  return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
};

type ExportPensionModalProps = {
  isOpen: boolean;
  toggle: () => void;
};

const ExportPensionModal: React.FC<ExportPensionModalProps> = ({
  isOpen,
  toggle,
}: ExportPensionModalProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const { run: exportNowPension } = useExportNowPension();
  const handleExportNowPension = async (values: ExportPensionFormValues) => {
    const isMonthly = values.payFrequency === 'MONTHLY';
    const response = await exportNowPension({
      taxYear: values.taxYear,
      taxPeriod: isMonthly ? undefined : values.taxPeriod,
      monthConfig: isMonthly ? values.taxPeriod : undefined,
      payFrequency: values.payFrequency,
      companyId: values.company?.value || '',
      includeAllPaid: values.includeAllPaid ?? false,
      payrollNoSource: values.payrollNoSource?.value ?? 'EMPLOYEE_ID',
    });
    if (response?.fileUrl) window.open(response?.fileUrl);
  };

  const { run: exportPapdisPension } = useExportPapdisPension();
  const handleExportPapdisPension = async (values: ExportPensionFormValues) => {
    const isMonthly = values.payFrequency === 'MONTHLY';
    const response = await exportPapdisPension({
      companyId: values.company?.value || '',
      taxYear: values.taxYear,
      taxPeriod: isMonthly ? undefined : values.taxPeriod,
      monthConfig: isMonthly ? values.taxPeriod : undefined,
      payFrequency: values.payFrequency,
    });
    if (response?.fileUrl) window.open(response?.fileUrl);
  };

  const { run: exportPension } = useExportPension();
  const handleExportPension = async (values: ExportPensionFormValues) => {
    const isMonthly = values.payFrequency === 'MONTHLY';
    const response = await exportPension({
      companyId: values.company?.value || '',
      taxYear: values.taxYear,
      taxPeriod: isMonthly ? undefined : values.taxPeriod,
      monthConfig: isMonthly ? values.taxPeriod : undefined,
      payFrequency: values.payFrequency,
      exportType: 'NEST_PENSION',
      skipScheduleCheck: true,
      nestHeader: {
        startDate: toIsoDate(values.nestStartDate),
        endDate: toIsoDate(values.nestEndDate),
        dueDate: toIsoDate(values.nestDueDate),
        paymentSource: values.nestPaymentSource,
        nestFrequency: values.nestFrequency,
      },
    });
    if (response?.fileUrl) window.open(response?.fileUrl);
  };

  const { run: exportPensionIreland } = useExportPensionIreland();
  const handleExportPensionIreland = async (
    values: ExportPensionFormValues,
  ) => {
    const response = await exportPensionIreland({
      companyId: values.company?.value || '',
      taxYear: values.taxYear,
      taxPeriod: values.taxPeriod,
      payFrequency: values.payFrequency,
    });
    if (response?.fileUrl) window.open(response?.fileUrl);
  };

  return (
    <AppModalContainer
      title={messages['common.exportPension']}
      isOpen={isOpen}
      toggle={toggle}
      minHeight="200px"
      className="!max-sm:w-[90vw]"
      width={'980px'}
    >
      {isIreland ? (
        <ExportPensionForm
          title={messages['common.pension']}
          onExport={handleExportPensionIreland}
        />
      ) : (
        <Row gutter={[16, 16]} align="top">
          {/* Left column: NOW + PAPDIS stacked, so PAPDIS fills the space
              next to the taller NEST card instead of being pushed below it. */}
          <Col sm={24} md={12} className="flex w-full flex-col gap-[16px]">
            <ExportPensionForm
              title={messages['common.nowPension']}
              onExport={handleExportNowPension}
              showNowPensionSettings
            />
            <ExportPensionForm
              title={messages['common.papdis']}
              onExport={handleExportPapdisPension}
              // PAPDIS is the generic contribution format: the NOW-like
              // providers plus SMART, which the NOW card's default filter omits.
              companyPensionProviderFilter={[
                ...nowLikePensionProviders,
                PensionProvidersNameEnum.SMART_PENSION,
              ]}
            />
          </Col>
          <Col sm={24} md={12} className="w-full">
            <ExportPensionForm
              title={messages['common.nestPension']}
              onExport={handleExportPension}
              showNestSettings
              // TEMPORARY (NX-5685) — REMOVE THE NOW-LIKE PROVIDERS BELOW once
              // the NEST manual-settings issue is fixed, leaving only
              // NEST_PENSION. Employers who are with NEST but cannot connect
              // (no API, manual settings not working yet) have to sit on a
              // NOW-like provider, so restricting this card to NEST_PENSION
              // locks them out of the NEST file they still owe NEST. The
              // backend applies no provider guard: exportType only picks the
              // file format, and the rows are still read and tagged with the
              // company's real provider.
              companyPensionProviderFilter={[
                PensionProvidersNameEnum.NEST_PENSION,
                ...nowLikePensionProviders,
              ]}
            />
          </Col>
        </Row>
      )}
    </AppModalContainer>
  );
};

export default ExportPensionModal;
